const META_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
const GTM_ID = import.meta.env.VITE_GTM_ID as string | undefined;
const GA4_ID = import.meta.env.VITE_GA4_ID as string | undefined;
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://eu.i.posthog.com';
const CONSENT_KEY = 'daien-cookies-consent';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
    posthog?: { capture: (event: string, props?: Record<string, unknown>) => void; [k: string]: unknown };
  }
}

export type CookieConsent = 'all' | 'essential';

export function getCookieConsent(): CookieConsent | null {
  if (typeof window === 'undefined') return null;
  const v = localStorage.getItem(CONSENT_KEY);
  return v === 'all' || v === 'essential' ? v : null;
}

export function hasMarketingConsent(): boolean {
  return getCookieConsent() === 'all';
}

export function acceptAllCookies() {
  if (typeof window === 'undefined') return;
  localStorage.setItem(CONSENT_KEY, 'all');
  initTracking();
}

export function rejectMarketingCookies() {
  if (typeof window === 'undefined') return;
  localStorage.setItem(CONSENT_KEY, 'essential');
}

export function resetCookieConsent() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(CONSENT_KEY);
}

let trackingInitialized = false;

export function initTracking() {
  if (typeof window === 'undefined') return;
  if (!hasMarketingConsent()) return;
  if (trackingInitialized) return;
  trackingInitialized = true;
  if (META_ID) initMetaPixel(META_ID);
  if (GTM_ID) initGTM(GTM_ID);
  if (GA4_ID) initGA4(GA4_ID);
  if (POSTHOG_KEY) initPostHog(POSTHOG_KEY, POSTHOG_HOST);
}

function initMetaPixel(id: string) {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  (function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode?.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq?.('init', id);
  window.fbq?.('track', 'PageView');
}

function initGTM(id: string) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
  document.head.appendChild(script);
}

function initGA4(id: string) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    // gtag dziala na obiekcie arguments (tak jak oficjalny snippet Google)
    window.dataLayer?.push(arguments as unknown as Record<string, unknown>);
  };
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);
  window.gtag('js', new Date());
  // send_page_view: false - to SPA, page view wysylamy recznie per trasa
  window.gtag('config', id, { send_page_view: false });
}

function initPostHog(key: string, host: string) {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  (function (t: Document, e: any) {
    let o: any, p: any, r: any;
    if (e.__SV) return;
    (window as any).posthog = e;
    e._i = [];
    e.init = function (i: any, s: any, a: any) {
      function g(t: any, e: string) {
        const o = e.split('.');
        if (o.length === 2) {
          t = t[o[0]];
          e = o[1];
        }
        t[e] = function () {
          t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
        };
      }
      p = t.createElement('script');
      p.type = 'text/javascript';
      p.async = true;
      p.src = s.api_host + '/static/array.js';
      r = t.getElementsByTagName('script')[0];
      if (r && r.parentNode) r.parentNode.insertBefore(p, r);
      else t.head.appendChild(p);
      let u = e;
      if (a !== undefined) u = e[a] = [];
      else a = 'posthog';
      u.people = u.people || [];
      o = 'init capture identify alias people set set_once register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset group reloadFeatureFlags onFeatureFlags getFeatureFlag getFeatureFlagPayload isFeatureEnabled'.split(
        ' ',
      );
      for (let n = 0; n < o.length; n++) g(u, o[n]);
      e._i.push([i, s, a]);
    };
    e.__SV = 1;
  })(document, (window as any).posthog || []);
  (window as any).posthog.init(key, {
    api_host: host,
    capture_pageview: false,
    person_profiles: 'identified_only',
  });
  /* eslint-enable */
}

export function trackLead(source: string) {
  window.fbq?.('track', 'Lead', { source });
  window.dataLayer?.push({ event: 'lead_submit', source });
  window.gtag?.('event', 'generate_lead', { source });
  window.posthog?.capture('lead', { source });
}

export function trackPageView(path: string) {
  window.fbq?.('track', 'PageView');
  window.dataLayer?.push({ event: 'page_view', path });
  window.gtag?.('event', 'page_view', { page_path: path });
  window.posthog?.capture('$pageview', { path });
}
